import React, { useState, useEffect, useMemo, useContext } from 'react'
import { useDecision } from '@optimizely/react-sdk'
import PropTypes from 'prop-types'
import { Formik, Form, ErrorMessage } from 'formik'
import { isInputField, hasLabel } from './formUtils/formUtils'
import { avantEndpoints } from '../../services/avantEndpoints'
import { trackingSubmitContentfulForm } from '../../helpers/segmentTracker'
import { formGridColumnsClass } from '../../helpers/gridUtils'
import SubmitButton from './fields/SubmitButton/SubmitButton'
import ContentfulModal from '../ContentfulModal/ContentfulModal'
import FieldComponentRenderer from './FieldComponentRenderer'
import odsWrapperContext from '../../context/odsWrapperContext'
import { passOptimizelyParam } from '../../helpers/pageUtils'

function ContentfulFormV3({ data }) {
  // can extract this to a prop in the future for other experiments
  const experimentName = 'dm_card_page'
  const [decision] = useDecision(experimentName)

  const { formModal: modalData } = data
  const {
    gridLayout: { formRows },
    formName
  } = data || {}
  const { odsApiData, setOdsApiData, odsData, setOfferStepV3 } = useContext(odsWrapperContext)

  const [openModal, setOpenModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isSsnEntryValid, setIsSsnEntryValid] = useState(true)
  const [shouldClearField, setShouldClearField] = useState(false)
  const [apiResponse, setApiResponse] = useState({})
  const [values, setValues] = useState({})
  const [segmentEventData, setSegmentEventData] = useState({
    segmentEvent: null,
    payload: null,
    text: null,
    formValues: null,
    requestResponse: null,
    productType: 'Card'
  })
  const isMyAvantCardPage =
    typeof window !== 'undefined' && window.location?.pathname
      ? window?.location?.pathname.startsWith('/content/myavantcard')
      : false

  // have to bind formik's setfieldValue to our own method so we can call it outside formik
  let mySetFieldValue = null
  useEffect(() => {
    if (shouldClearField) {
      mySetFieldValue('last_4_of_ssn', '')
      setShouldClearField(false)
    }
  }, [shouldClearField])

  const handleModal = () => {
    setOpenModal(false)
    setApiResponse(null)
  }

  // using the useMemo Hook will help to avoid unnecessary re-renders
  const submitButtonComp = useMemo(
    () =>
      data.formButton?.map(button => {
        setSegmentEventData({
          ...segmentEventData,
          segmentEvent: button.segmentEvent,
          payload: button.payload,
          text: button.text
        })
        return (
          <SubmitButton buttonName={button.text} loading={loading} key={segmentEventData.text} />
        )
      }),
    [data.formButton, loading]
  )

  async function submitValues(val) {
    setLoading(true)
    try {
      setValues(val)
      // Override formName for MyAvant card page to match avantEndpoints switch case
      let actualFormName = formName
      if (isMyAvantCardPage && formName !== 'myavantcard') {
        actualFormName = 'myavantcard'
      }
      const obj = {
        values: val,
        product_category: segmentEventData?.payload?.product_type,
        formName: actualFormName,
        odsData,
        odsApiData
      }
      let res = await avantEndpoints(obj)
      if (isMyAvantCardPage) {
        res = passOptimizelyParam(res, decision, experimentName)
      }
      setApiResponse(res)
    } finally {
      setLoading(false)
    }
  }

  useMemo(() => {
    if (apiResponse && Object.keys(apiResponse).length !== 0) {
      setSegmentEventData({
        ...segmentEventData,
        formValues: values,
        requestResponse: apiResponse.requestResponse
      })
    }
  }, [apiResponse])

  const handleODSFormNextStep = (response, responseDataForNextStep) => {
    if (response.offerType === 'none' || response.offerType === 'suppression') {
      setOfferStepV3('failedOffer')
    } else if (response.offerType === 'digital') {
      setOdsApiData(responseDataForNextStep)
      setOfferStepV3('finalOffer')
    }
  }

  const handleSSNModalNextStep = (response, responseDataForNextStep) => {
    // user types in correct last 4 immediately on first attempt
    if (response.ssnSuccess === true) {
      // we don't want to show the user an offer if it's already expired
      if (response.offerExpired) {
        setOfferStepV3('failedOffer')
      } else {
        setOdsApiData(responseDataForNextStep)
        setOfferStepV3('finalOffer')
      }
      // user enters wrong SSN
    } else if (response.ssnSuccess === null) {
      setIsSsnEntryValid(false)
      setShouldClearField(true)
    }
    // user hits max ssn entry retries
    else if (response.ssnSuccess === false) {
      setOfferStepV3('failedOffer')
    }
  }

  // These only run when the component is mounted in a browser, so we can be safe in the knowledge that window does indeed exist
  // when the trackingSubmit function triggers
  useEffect(() => {
    if (apiResponse && Object.keys(apiResponse).length !== 0) {
      setOpenModal(apiResponse.modal)

      const responseDataForNextStep = {
        cardOffer: apiResponse.cardOffer,
        prefillLink: apiResponse.prefillLink,
        isMailOffer: apiResponse.isMailOffer,
        returningCustomerFlag: apiResponse.returningCustomerFlag,
        customerName: apiResponse.customerName
      }
      if (formName === 'ODSForm') {
        handleODSFormNextStep(apiResponse, responseDataForNextStep)
      } else if (formName === 'ODS_SSN') {
        handleSSNModalNextStep(apiResponse, responseDataForNextStep)
      }

      trackingSubmitContentfulForm(apiResponse, segmentEventData, formName).catch(err =>
        console.error(err)
      )
    }
  }, [apiResponse])

  // Here the form fields are created, the formGridColumnsClass function adds the size
  // that the field will have inside the form-row class
  const formFieldsComp = formRows.map(row => (
    <div className="form__row" key={row.id}>
      {row.formColumns.map(item => {
        return (
          <div className={`form__group ${formGridColumnsClass(item.columnNumber)}`} key={item.id}>
            {hasLabel(item.formField)}
            <FieldComponentRenderer field={item.formField} />
            {/* only show one type of validation message at a time */}
            {isInputField(item.formField) && isSsnEntryValid ? (
              <div className="error-messages">
                <ErrorMessage name={item.formField.fieldLabel.toLowerCase().replace(/\s/g, '_')} />
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  ))

  return (
    <>
      {openModal && apiResponse ? (
        <ContentfulModal
          modalData={modalData}
          apiResponse={apiResponse}
          handleModal={handleModal}
        />
      ) : null}
      <Formik
        enableReinitialize
        initialValues={values}
        onSubmit={async vals => {
          await submitValues(vals)
        }}
        validateOnChange
      >
        {({ setFieldValue }) => {
          mySetFieldValue = setFieldValue
          return (
            <Form>
              {formFieldsComp}
              {isSsnEntryValid ? null : <div className="invalid-entry">Invalid Entry</div>}
              {submitButtonComp}
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

ContentfulFormV3.propTypes = {
  data: PropTypes.object.isRequired
}

export { query as data } from './formQuery'

export default ContentfulFormV3
